import { useQuery } from '@tanstack/react-query';
import { visionsApi, goalsApi, activitiesApi } from '../../api/client';
import { useTeamStore } from '../../stores/teamStore';
import { LIST_REFETCH_INTERVAL_MS } from '../../constants';

/**
 * Fetches visions, goals, and activities in parallel.
 * Returns raw (encrypted) data — decryption is handled by the screen component.
 */
export function useVisionsList() {
  const activeTeamId = useTeamStore((s) => s.activeTeamId);
  const teamParams = activeTeamId ? { team_id: activeTeamId } : undefined;

  return useQuery({
    queryKey: ['visions', 'list-with-deps', activeTeamId ?? 'personal'],
    refetchOnMount: 'always',
    refetchOnReconnect: true,
    // Cross-device convergence: another device's change shows up here within
    // one refetch interval while the screen is visible, and at once when the
    // app or tab regains focus — regardless of the 2-minute staleTime.
    refetchOnWindowFocus: 'always',
    refetchInterval: LIST_REFETCH_INTERVAL_MS,
    refetchIntervalInBackground: false,
    queryFn: async () => {
      const [visionsRes, goalsRes, activitiesRes] = await Promise.all([
        visionsApi.list(teamParams),
        goalsApi.list(teamParams),
        activitiesApi.list(teamParams),
      ]);
      return {
        visions: visionsRes.data,
        goals: goalsRes.data,
        activities: activitiesRes.data,
      };
    },
  });
}
